// Core
import { useState, useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// Assets
import { apiService } from 'services/ApiService';

// Components
import StationEditContent from './components/StationEditContent';
import useFormPageRedirect from '../../../hooks/useFormPageRedirect';

const stationSchema = z.object({
  name: z.string().min(1),
  externalId: z.string().min(1),
  phone: z.string().optional().nullable(),
  location: z.object({
    locationAddress: z.string().optional().nullable(),
    lat: z.number().optional().nullable(),
    lon: z.number().optional().nullable(),
  }),
});

const StationsEdit = () => {
  const history = useHistory();
  const { id } = useParams();

  const [errorCard, setErrorCard] = useState();

  const form = useForm({
    resolver: zodResolver(stationSchema),
    defaultValues: {
      name: '',
      externalId: '',
      phone: '',
      location: {
        locationAddress: '',
      },
    },
  });

  const { reset, handleSubmit } = form;

  const { canRead } = useFormPageRedirect({
    id,
    createPermissionKey: 'POST:/admin/stations',
    readPermissionKey: 'GET:/admin/stations/:id',
    redirectPath: '/stations',
  });

  const { isLoading, isError } = useQuery({
    queryKey: ['station', id, canRead],
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnError: false,
    queryFn: async () => {
      if (id === 'new') {
        reset({
          name: '',
          externalId: '',
          phone: '',
          location: {
            locationAddress: '',
          },
        });
        return true;
      }
      if (canRead) {
        const response = await apiService.getStation({ id });
        const station = response?.data;
        reset({
          name: station?.name,
          externalId: station?.externalId,
          phone: station?.phone,
          location: {
            locationAddress: station?.locationAddress,
            lat: station?.lat,
            lon: station?.lon,
          },
        });
      }
      return true;
    },
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      const payload = {
        name: data.name,
        externalId: data.externalId,
        ...(data.phone && { phone: data.phone }),
        ...(data.location.locationAddress
          ? {
              locationAddress: data.location.locationAddress,
              ...(data.location.lat !== undefined && {
                lat: data.location.lat,
              }),
              ...(data.location.lon !== undefined && {
                lon: data.location.lon,
              }),
            }
          : {
              locationAddress: null,
              lat: null,
              lon: null,
            }),
      };
      if (id === 'new') {
        return apiService.createStation(payload);
      } else {
        return apiService.updateStation({ id, ...payload });
      }
    },
    onSuccess: (res) => {
      if (
        res.data === false ||
        !(res.statusCode >= 200 && res.statusCode < 300)
      ) {
        setErrorCard(res.message);
        return;
      }
      history.push('/stations');
    },
    onError: () => {
      setErrorCard('Something went wrong.');
    },
  });

  const onSubmit = (values) => {
    mutation.mutate(values);
  };

  const onError = (errors) => {
    console.log('Validation errors:', errors);
  };

  const handleDelete = useCallback(async () => {
    await apiService.deleteStation({ id });
    reset({
      name: '',
      externalId: '',
      phone: '',
      location: {
        locationAddress: '',
      },
    });
    history.push('/stations');
  }, [history, id, reset]);

  return isError ? (
    <div>Not Found</div>
  ) : isLoading ? (
    <div>Loading...</div>
  ) : (
    <StationEditContent
      handleDelete={handleDelete}
      isNew={id === 'new'}
      form={form}
      onSubmit={handleSubmit(onSubmit, onError)}
      isSubmitting={mutation.isLoading}
      errorCard={errorCard}
    />
  );
};

export default StationsEdit;
